"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import type { LearnedPreference, MemoryCandidate } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { LoadingState, ErrorState } from "@/components/state"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useI18n } from "@/i18n"
import type { Messages } from "@/i18n/types"

type MemoryMessages = Messages["moreProfile"]["memory"]
type EffectKey = keyof Pick<
  MemoryMessages,
  | "effectIngredientDislike"
  | "effectIngredientLike"
  | "effectRecipeDislike"
  | "effectRecipeLike"
  | "effectCuisine"
  | "effectNote"
  | "effectOther"
>

/**
 * candidateType/kind (server vocabulary — infra-cdk/lambdas/bitebalance-api/
 * memory.ts's mapCandidateTypeToSoftPreferenceKind) → the i18n key for the
 * plain-language "effect on your plans" line. This mirrors the server's
 * bucketing on purpose (not imported from it — there is no shared package
 * boundary between infra-cdk lambdas and the frontend) so the wording never
 * contradicts what actually happens to generation scoring: a kind this page
 * can't recognize gets the honest "does not yet change plan generation"
 * copy rather than a guessed one.
 */
function effectKeyFor(kind: string): EffectKey {
  switch (kind) {
    case "ingredient_dislike":
      return "effectIngredientDislike"
    case "ingredient_like":
      return "effectIngredientLike"
    case "recipe_dislike":
      return "effectRecipeDislike"
    case "recipe_like":
      return "effectRecipeLike"
    case "cuisine":
    case "cuisine_preference":
      return "effectCuisine"
    case "personal_note":
    case "tone_preference":
      return "effectNote"
    default:
      return "effectOther"
  }
}

function originLabel(origin: string, t: (key: string) => string): string {
  return origin === "amy_chat" ? t("moreProfile.memory.originAmyChat") : origin
}

/**
 * /app/more/memory — T15 Memory Center: pending memory candidates Amy
 * proposed (confirm/reject) and confirmed learned preferences (wording,
 * origin, effect, delete). Confirming here always uses choice "durable" —
 * "use this once" is a chat-only concept (Amy simply never proposes a
 * candidate for a one-off request), so there is deliberately no "once"
 * control in this UI. Deleting removes the Postgres learned_preferences AND
 * soft_preferences rows (infra-cdk/lambdas/bitebalance-api/memory.ts); it
 * does not yet remove any mirrored AgentCore Memory fact — that lands in
 * T18 (documented gap, not implemented here).
 */
export default function MemoryPage() {
  const api = useApiClient()
  const { t } = useI18n()
  const [candidates, setCandidates] = useState<MemoryCandidate[]>([])
  const [preferences, setPreferences] = useState<LearnedPreference[]>([])
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const [deleteTarget, setDeleteTarget] = useState<LearnedPreference | null>(null)
  /** Saved step-7 tastes (owner confusion 2026-08-29: "Amy saved my bacon
   * dislike but Memory says nothing remembered" — tastes live in the
   * preference store, not chat notes; show BOTH here). */
  const [tastes, setTastes] = useState<{ dislikes: string[]; likes: string[] } | null>(null)

  function load(onCancelled: () => boolean = () => false) {
    setState("loading")
    Promise.all([
      api.getMemoryCandidates(),
      api.getLearnedPreferences(),
      api.getOnboarding().catch(() => null),
    ])
      .then(([candidatesRes, preferencesRes, onboarding]) => {
        if (onCancelled()) return
        setCandidates(candidatesRes.candidates.filter(c => c.status === "pending"))
        setPreferences(preferencesRes.preferences)
        const step7 = onboarding?.answers.find(a => a.step === 7)?.answer as
          | { dislikes?: string[] | "none"; likes?: string[] | "none" }
          | undefined
        setTastes({
          dislikes: Array.isArray(step7?.dislikes) ? step7.dislikes : [],
          likes: Array.isArray(step7?.likes) ? step7.likes : [],
        })
        setState("ready")
      })
      .catch(() => {
        if (!onCancelled()) setState("error")
      })
  }

  useEffect(() => {
    let cancelled = false
    load(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [api])

  async function handleConfirm(candidate: MemoryCandidate) {
    setBusyId(candidate.id)
    setRowError(e => ({ ...e, [candidate.id]: "" }))
    try {
      await api.confirmMemoryCandidate(candidate.id, { choice: "durable" })
      load()
    } catch {
      setRowError(e => ({ ...e, [candidate.id]: t("moreProfile.memory.confirmError") }))
    } finally {
      setBusyId(null)
    }
  }

  async function handleReject(candidate: MemoryCandidate) {
    setBusyId(candidate.id)
    setRowError(e => ({ ...e, [candidate.id]: "" }))
    try {
      await api.rejectMemoryCandidate(candidate.id)
      load()
    } catch {
      setRowError(e => ({ ...e, [candidate.id]: t("moreProfile.memory.rejectError") }))
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setBusyId(deleteTarget.id)
    try {
      await api.deleteLearnedPreference(deleteTarget.id)
      setDeleteTarget(null)
      load()
    } catch {
      setRowError(e => ({ ...e, [deleteTarget.id]: t("moreProfile.memory.deleteError") }))
      setDeleteTarget(null)
    } finally {
      setBusyId(null)
    }
  }

  if (state === "loading") return <LoadingState />
  if (state === "error") return <ErrorState onRetry={() => load()} />

  return (
    <section
      aria-labelledby="memory-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <MoreSubpageHeader title={t("moreProfile.memory.title")} headingId="memory-heading" />

      <div className="rounded-bb-xl border border-line bg-paper p-4">
        <h2 className="font-semibold text-ink">{t("moreProfile.memory.pendingTitle")}</h2>
        {candidates.length === 0 ? (
          <p className="mt-2 text-sm text-ink-2">{t("moreProfile.memory.pendingEmpty")}</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-3">
            {candidates.map(candidate => (
              <li
                key={candidate.id}
                className="flex flex-col gap-2 rounded-bb-md border border-line bg-cream p-3"
              >
                <span className="font-medium text-ink">{candidate.candidateValue}</span>
                <p className="text-xs text-ink-2">{candidate.proposedEffectDescription}</p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="min-h-9"
                    disabled={busyId === candidate.id}
                    onClick={() => void handleConfirm(candidate)}
                  >
                    {t("moreProfile.memory.confirmButton")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-9"
                    disabled={busyId === candidate.id}
                    onClick={() => void handleReject(candidate)}
                  >
                    {t("moreProfile.memory.rejectButton")}
                  </Button>
                </div>
                {rowError[candidate.id] && (
                  <p className="text-xs text-destructive">{rowError[candidate.id]}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-bb-xl border border-line bg-paper p-4">
        <h2 className="font-semibold text-ink">{t("moreProfile.memory.tastesTitle")}</h2>
        {tastes && (tastes.dislikes.length > 0 || tastes.likes.length > 0) ? (
          <div className="mt-3 flex flex-col gap-3">
            {tastes.dislikes.length > 0 && (
              <div>
                <p className="text-xs font-bold tracking-wide text-ink-2 uppercase">
                  {t("moreProfile.memory.tastesDislikes")}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {tastes.dislikes.map(item => (
                    <span
                      key={item}
                      className="rounded-bb-full bg-coral/15 px-2.5 py-1 text-sm text-ink capitalize"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {tastes.likes.length > 0 && (
              <div>
                <p className="text-xs font-bold tracking-wide text-ink-2 uppercase">
                  {t("moreProfile.memory.tastesLikes")}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {tastes.likes.map(item => (
                    <span
                      key={item}
                      className="rounded-bb-full bg-lime/50 px-2.5 py-1 text-sm text-ink capitalize"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t("moreProfile.memory.tastesHint")}</p>
          </div>
        ) : (
          <p className="mt-2 text-sm text-ink-2">{t("moreProfile.memory.tastesEmpty")}</p>
        )}
      </div>

      <div className="rounded-bb-xl border border-line bg-paper p-4">
        <h2 className="font-semibold text-ink">{t("moreProfile.memory.confirmedTitle")}</h2>
        {preferences.length === 0 ? (
          <p className="mt-2 text-sm text-ink-2">{t("moreProfile.memory.confirmedEmpty")}</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-3">
            {preferences.map(pref => (
              <li
                key={pref.id}
                className="flex flex-col gap-2 rounded-bb-md border border-line bg-cream p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-medium text-ink">{pref.text}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-9"
                    disabled={busyId === pref.id}
                    onClick={() => setDeleteTarget(pref)}
                  >
                    {t("moreProfile.memory.deleteButton")}
                  </Button>
                </div>
                <p className="text-xs text-ink-2">
                  {t("moreProfile.memory.originLabel")}: {originLabel(pref.origin, t)}
                </p>
                <p className="text-xs text-ink-2">
                  {t("moreProfile.memory.effectLabel")}:{" "}
                  {t(`moreProfile.memory.${effectKeyFor(pref.kind)}`)}
                </p>
                {rowError[pref.id] && (
                  <p className="text-xs text-destructive">{rowError[pref.id]}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("moreProfile.memory.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("moreProfile.memory.deleteConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              {t("moreProfile.memory.cancelButton")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busyId === deleteTarget?.id}
              onClick={() => void handleDelete()}
            >
              {t("moreProfile.memory.deleteConfirmButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
